import React from "react"
import { Button, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faPen,
  faExpand,
  faCompress,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import { useDashboardLayoutStore } from "stores/dashboardLayoutStore"
import FilterBar from "features/Dashboard/components/FilterBar/FilterBar.component"
import PageTabs from "features/Dashboard/components/PageTabs/PageTabs.component"
import DummyBadge from "features/DummyData/components/DummyBadge.component"
import { useQuestionnaireProjectId } from "features/DummyData/api/useDummyData"

interface ViewerHeaderProps {
  onExport: () => void
}

/**
 * View mode's slim header: one row that never wraps, with the title (one
 * line, truncated with a tooltip; a "Dummy data" badge follows it while the
 * project's data is dummy data) and page tabs, then Export, Build and full
 * screen on the right; the filter bar has its own line below. Viewing has no
 * main action, so no button is solid.
 */
const ViewerHeader: React.FC<ViewerHeaderProps> = ({ onExport }) => {
  const title = useDashboardStore((s) => s.document.title)
  const setMode = useDashboardStore((s) => s.setMode)
  const isMaximized = useDashboardLayoutStore((s) => s.isMaximized)
  const toggleMaximized = useDashboardLayoutStore((s) => s.toggleMaximized)
  // A project without a questionnaire never has dummy data: nothing to read.
  const projectId = useDashboardStore((s) => s.projectId)
  const dummyFor = useQuestionnaireProjectId(projectId)

  return (
    <header className="dq-viewer-header">
      <div className="dq-viewer-row flex-nowrap">
        <Tooltip title={title} placement="bottomLeft">
          <h1 className="dq-viewer-title">{title}</h1>
        </Tooltip>
        <DummyBadge projectId={dummyFor} />
        <div className="dq-viewer-pages">
          <PageTabs readOnly />
        </div>
        <div className="ml-auto flex flex-none items-center gap-2">
          <Tooltip title="Export" placement="bottom">
            <Button
              aria-label="Export"
              icon={<FontAwesomeIcon icon={faFileExport} />}
              onClick={onExport}
            />
          </Tooltip>
          <Tooltip title="Edit this dashboard" placement="bottom">
            <Button
              aria-label="Build"
              icon={<FontAwesomeIcon icon={faPen} />}
              onClick={() => setMode("build")}
            />
          </Tooltip>
          <Tooltip
            title={isMaximized ? "Exit full screen (Esc)" : "Expand to full screen"}
            placement="bottom"
          >
            <button
              type="button"
              className="dq-btn dq-btn--icon"
              aria-label={isMaximized ? "Exit full screen" : "Enter full screen"}
              aria-pressed={isMaximized}
              onClick={toggleMaximized}
            >
              <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} />
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="dq-viewer-filters">
        <FilterBar />
      </div>
    </header>
  )
}

export default ViewerHeader
